import { intervalToDuration, differenceInSeconds } from "date-fns"
import { formatDuration } from "@/shared/utils/date-fn"
import type { DailyCalendarEvent, DailyActivity, DailyEvent } from "../../entities/daily-calendar"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useDailyCalendarEvents } from "../../stores/use-daily-calendar-events"
import { overTimeInSeconds } from "@/modules/study/constants/calendar-overtime"

function calculateStudyTimeInSeconds(events: DailyCalendarEvent[]): number {
  const activities = events.filter(
    (event): event is DailyActivity => event.dailyCalendarEventType === "activity",
  )

  let totalSeconds = 0

  activities.forEach((activity) => {
    const [hours, minutes, seconds] = activity.estimatedDuration.split(":").map(Number)
    totalSeconds += hours * 3600
    totalSeconds += minutes * 60
    totalSeconds += seconds || 0
  })

  return totalSeconds
}

function calculateDeclaredStudyTimeInSeconds(events: DailyCalendarEvent[]): number {
  const studyEvents = events.filter(
    (event): event is DailyEvent =>
      event.dailyCalendarEventType === "event" && event.parent.category === "study_time",
  )

  let totalSeconds = 0

  studyEvents.forEach((event) => {
    const startTime = new Date(event.startDate)
    const endTime = new Date(event.endDate)
    const durationSeconds = differenceInSeconds(endTime, startTime)
    totalSeconds += durationSeconds
  })

  return totalSeconds
}

export default function DailyCalendarStudyTime() {
  const { filteredEvents } = useDailyCalendarEvents()

  const totalStudyTimeInSeconds = calculateStudyTimeInSeconds(filteredEvents)
  const duration = intervalToDuration({ start: 0, end: totalStudyTimeInSeconds * 1000 })
  const formattedDuration = formatDuration(duration).min()

  const totalDeclaredStudyTimeInSeconds = calculateDeclaredStudyTimeInSeconds(filteredEvents)
  const isOverloaded = totalStudyTimeInSeconds - overTimeInSeconds > totalDeclaredStudyTimeInSeconds

  return formattedDuration ? (
    <div className="flex items-center justify-center gap-4">
      <span className="text-sm text-gray-800">Tiempo de estudio:</span>
      {isOverloaded ? (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex w-fit items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 animate-pulse-shadow rounded-full bg-destructive"></span>
                <span className="text-sm text-destructive">{formattedDuration}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent
              className="w-[200px] rounded-lg border border-neutral-200 bg-white p-2 text-xs shadow-lg"
              align="end"
              side="bottom"
            >
              <p>Superaste las horas de estudio declaradas. Ajusta tus horas en Mi agenda.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <span className="text-sm text-gray-800">{formattedDuration}</span>
      )}
    </div>
  ) : null
}
